import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FaMapMarkerAlt,
  FaStar,
  FaWifi,
  FaParking,
  FaSmokingBan,
  FaCheck,
  FaCalendarAlt,
  FaUsers,
  FaInfoCircle,
  FaArrowLeft,
  FaSwimmingPool,
  FaTv,
  FaUtensils,
  FaSnowflake
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import Navbar from '../components/Navbar';
import { getPropertyImage } from '../utils/imageHelper';
import { isDiscountVisibleToGuest } from '../utils/offerStatus';
import './BookingPage.css';

const BookingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { property, checkIn, checkOut, checkInTime, checkOutTime, guests, guestData, numberOfNights, pricing, discount } = location.state || {};

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    confirmEmail: '',
    country: 'India',
    phone: '',
    bookingForSelf: true,
    bookingForSomeone: false,
    receiveUpdates: true,
    travelingForWork: false,
    specialRequests: '',
    arrivalTime: '',
    cribs: false,
    extraBeds: false
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [autoFilled, setAutoFilled] = useState(false);
  const [showConditionsModal, setShowConditionsModal] = useState(false);
  const [showOfferConfirmation, setShowOfferConfirmation] = useState(false);

  // Load current user and auto-fill form
  useEffect(() => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const user = JSON.parse(storedUser);
        setCurrentUser(user);
        
        // Auto-fill form with user data
        if (user && !autoFilled) {
          setFormData(prev => ({
            ...prev,
            firstName: user.firstName || user.name?.split(' ')[0] || '',
            lastName: user.lastName || user.name?.split(' ').slice(1).join(' ') || '',
            email: user.email || '',
            confirmEmail: user.email || '',
            phone: user.phone || user.phoneNumber || '',
            country: user.country || 'India'
          }));
          setAutoFilled(true);
        }
      }
    } catch (error) {
      console.error('Error loading user:', error);
    }
  }, [autoFilled]);

  // Redirect if no property data
  useEffect(() => {
    if (!property || !checkIn || !checkOut) {
      navigate('/');
    }
  }, [property, checkIn, checkOut, navigate]);

  // Compute for useMemo (safe when property/checkIn/checkOut missing) - hooks must run before any return
  const calculatedNumberOfNights = (checkIn && checkOut)
    ? (numberOfNights || Math.ceil((new Date(checkOut) - new Date(checkIn)) / (1000 * 60 * 60 * 24)))
    : 0;
  const basePrice = property ? (property.pricePerNight || 0) * calculatedNumberOfNights : 0;
  const serviceFeePercent = property?.serviceFeePercent ?? 10;

  const computedDiscountFromProperty = React.useMemo(() => {
    if (!isDiscountVisibleToGuest(property?.discount) || calculatedNumberOfNights === 0) return null;
    const d = property.discount;
    const pricePerNight = property.pricePerNight || 0;
    const originalTotal = pricePerNight * calculatedNumberOfNights;
    let discountedPerNight = pricePerNight;
    let discountText = '';
    if (d.type === 'percentage') {
      const perNight = (pricePerNight * d.value) / 100;
      const maxD = d.maxDiscountAmount || Infinity;
      discountedPerNight = pricePerNight - Math.min(perNight, maxD);
      discountText = `${d.value}% OFF`;
    } else if (d.type === 'fixed') {
      discountedPerNight = Math.max(0, pricePerNight - d.value);
      discountText = `₹${d.value} OFF`;
    }
    const discountedTotal = discountedPerNight * calculatedNumberOfNights;
    const discountAmount = originalTotal - discountedTotal;
    const serviceFee = Math.round(discountedTotal * (serviceFeePercent / 100));
    const taxes = Math.round(discountedTotal * 0.12);
    return {
      originalPrice: originalTotal,
      discountedPrice: discountedTotal,
      discountAmount,
      serviceFee,
      taxes,
      finalTotal: discountedTotal + serviceFee + taxes,
      discount: { ...d, isActive: true, discountText }
    };
  }, [property, calculatedNumberOfNights, serviceFeePercent]);

  if (!property || !checkIn || !checkOut) {
    return null;
  }

  const useComputedDiscount = isDiscountVisibleToGuest(property?.discount) && (!pricing || pricing.discountAmount === 0) && computedDiscountFromProperty;
  const pricingData = useComputedDiscount
    ? {
        originalPrice: computedDiscountFromProperty.originalPrice,
        discountedPrice: computedDiscountFromProperty.discountedPrice,
        discountAmount: computedDiscountFromProperty.discountAmount,
        serviceFee: computedDiscountFromProperty.serviceFee,
        taxes: computedDiscountFromProperty.taxes,
        finalTotal: computedDiscountFromProperty.finalTotal
      }
    : (pricing || {
        originalPrice: basePrice,
        discountedPrice: basePrice,
        discountAmount: 0,
        serviceFee: Math.round(basePrice * (serviceFeePercent / 100)),
        taxes: Math.round(basePrice * 0.12),
        finalTotal: 0
      });
  if (!pricingData.finalTotal) {
    pricingData.finalTotal = pricingData.discountedPrice + pricingData.serviceFee + pricingData.taxes;
  }

  const effectiveDiscount = useComputedDiscount ? computedDiscountFromProperty.discount : (isDiscountVisibleToGuest(discount) ? discount : null);

  const formatTimeForDisplay = (timeValue, fallback = '') => {
    if (!timeValue || typeof timeValue !== 'string') return fallback;

    const hhmmMatch = timeValue.trim().match(/^([01]?\d|2[0-3]):([0-5]\d)$/);
    if (hhmmMatch) {
      const hours = Number(hhmmMatch[1]);
      const minutes = Number(hhmmMatch[2]);
      const displayDate = new Date();
      displayDate.setHours(hours, minutes, 0, 0);
      return displayDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    }

    const amPmMatch = timeValue.trim().match(/^(\d{1,2}):([0-5]\d)\s*(AM|PM)$/i);
    if (amPmMatch) {
      return `${Number(amPmMatch[1])}:${amPmMatch[2]} ${amPmMatch[3].toUpperCase()}`;
    }

    return fallback || timeValue;
  };

  const checkInTimeDisplay = formatTimeForDisplay(checkInTime, property?.checkInTime || '3:00 PM');
  const checkOutTimeDisplay = formatTimeForDisplay(checkOutTime, property?.checkOutTime || '11:00 AM');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleRadioChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value,
      bookingForSelf: name === 'bookingForSelf' ? value : !value,
      bookingForSomeone: name === 'bookingForSomeone' ? value : !value
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (formData.email !== formData.confirmEmail) {
      newErrors.confirmEmail = 'Emails do not match';
    }
    if (formData.phone && !/^[0-9]{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
    }
    if (formData.specialRequests.length > 250) {
      newErrors.specialRequests = 'Special requests cannot exceed 250 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Show offer confirmation popup if there's a discount
    if (effectiveDiscount) {
      setShowOfferConfirmation(true);
      return;
    }

    // Proceed with booking
    proceedWithBooking();
  };

  const proceedWithBooking = async () => {
    setLoading(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Navigate to payment page with updated pricing (pass effective discount so payment shows correct amount)
      navigate('/payment', {
        state: {
          property,
          checkIn,
          checkOut,
          checkInTime,
          checkOutTime,
          guests,
          guestData,
          numberOfNights: calculatedNumberOfNights,
          pricing: pricingData,
          discount: effectiveDiscount || discount,
          totalPrice: pricingData.finalTotal,
          formData
        }
      });
    } catch (error) {
      console.error('Booking error:', error);
      toast.error('Failed to process booking. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOfferConfirmation = (confirmed) => {
    setShowOfferConfirmation(false);
    if (confirmed) {
      proceedWithBooking();
    }
  };

  const getAmenityIcon = (amenity) => {
    const a = String(amenity || '').toLowerCase();
    if (a.includes('wifi') || a.includes('internet')) return <FaWifi />;
    if (a.includes('parking')) return <FaParking />;
    if (a.includes('pool') || a.includes('swimming')) return <FaSwimmingPool />;
    if (a.includes('tv') || a.includes('television')) return <FaTv />;
    if (a.includes('kitchen') || a.includes('cooking')) return <FaUtensils />;
    if (a.includes('air') || a.includes('ac') || a.includes('conditioning') || a.includes('heating')) return <FaSnowflake />;
    if (a.includes('smoking') || a.includes('non-smoking')) return <FaSmokingBan />;
    return <FaCheck />;
  };

  const getSafeString = (value, fallback = '') => {
    if (!value) return fallback;
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return String(value);
    if (typeof value === 'object') {
      if (value.name) return String(value.name);
      if (value.value) return String(value.value);
      if (value.city) return String(value.city);
      return fallback;
    }
    return String(value);
  };

  return (
    <div className="booking-page">
      <Navbar />
      
      {/* Progress Indicator */}
      <div className="booking-progress">
        <div className="progress-bar-container">
          <div className="progress-step active">
            <span className="step-number">1</span>
            <span className="step-label">Your details</span>
          </div>
          <div className="progress-line"></div>
          <div className="progress-step">
            <span className="step-number">2</span>
            <span className="step-label">Final step</span>
          </div>
        </div>
      </div>

      <div className="booking-container">
        {/* Left Column - Booking Summary */}
        <div className="booking-left-column">
          {/* Property Overview */}
          <div className="booking-property-card">
            <div className="property-image">
              <img 
                src={getPropertyImage(property, 'https://images.unsplash.com/photo-1501117716987-c8e1ecb2101d?w=1200&q=80')} 
                alt={getSafeString(property.name)}
              />
            </div>
            <div className="property-info">
              <h2 className="property-name">{getSafeString(property.name)}</h2>
              <div className="property-location">
                <FaMapMarkerAlt />
                <span>{getSafeString(property.city, 'Unknown Location')}, {getSafeString(property.state, 'India')}</span>
              </div>
              <div className="property-rating">
                <FaStar />
                <span>{property.rating || '7.5'} {property.rating >= 8 ? 'Superb' : property.rating >= 7 ? 'Good' : 'Pleasant'}</span>
              </div>
              {property.amenities && property.amenities.length > 0 && (
                <div className="property-amenities">
                  {property.amenities.slice(0, 4).map((amenity, idx) => (
                    <span key={idx}>{getAmenityIcon(amenity)} {amenity}</span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Booking Details */}
          <div className="booking-details-card">
            <h3>Your booking details</h3>
            <div className="booking-detail-row">
              <FaCalendarAlt />
              <div>
                <div className="detail-label">Check-in</div>
                <div className="detail-value">{new Date(checkIn).toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })} · {checkInTimeDisplay}</div>
              </div>
            </div>
            <div className="booking-detail-row">
              <FaCalendarAlt />
              <div>
                <div className="detail-label">Check-out</div>
                <div className="detail-value">{new Date(checkOut).toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })} · {checkOutTimeDisplay}</div>
              </div>
            </div>
            <div className="booking-detail-row">
              <FaUsers />
              <div>
                <div className="detail-label">Guests</div>
                <div className="detail-value">{numberOfNights} {numberOfNights === 1 ? 'night' : 'nights'}, 1 room for {guests} {guests === 1 ? 'adult' : 'adults'}</div>
              </div>
            </div>
          </div>

          {/* Price Summary */}
          <div className="booking-price-card">
            {/* Discount Badge */}
            {effectiveDiscount && (
              <div className="booking-discount-badge">
                <span className="discount-badge-text">{effectiveDiscount.badge || 'Special Offer'}</span>
                <span className="discount-title">{effectiveDiscount.title}</span>
              </div>
            )}

            <div className="price-total">
              <span className="total-label">Your price summary</span>
              <span className="total-amount">₹{pricingData.finalTotal.toLocaleString()}</span>
            </div>
            
            {/* Savings highlight */}
            {effectiveDiscount && pricingData.discountAmount > 0 && (
              <div className="savings-highlight-booking">
                <span>🎉 You save ₹{pricingData.discountAmount.toLocaleString()} with this offer!</span>
              </div>
            )}

            <div className="price-note">
              <FaInfoCircle />
              <span>Includes ₹{pricingData.taxes.toLocaleString()} in taxes and charges</span>
            </div>
            
            <div className="price-breakdown">
              <div className="breakdown-row">
                <span>Room price ({calculatedNumberOfNights} nights):</span>
                <span>₹{(effectiveDiscount ? pricingData.originalPrice : pricingData.discountedPrice).toLocaleString()}</span>
              </div>
              
              {/* Discount row */}
              {effectiveDiscount && pricingData.discountAmount > 0 && (
                <div className="breakdown-row discount-row-booking">
                  <span className="discount-label-booking">
                    <span className="discount-icon-booking">🎯</span>
                    {effectiveDiscount.title} ({effectiveDiscount.discountText})
                  </span>
                  <span className="discount-amount-booking">-₹{pricingData.discountAmount.toLocaleString()}</span>
                </div>
              )}
              
              {/* Subtotal after discount */}
              {effectiveDiscount && pricingData.discountAmount > 0 && (
                <div className="breakdown-row subtotal-row-booking">
                  <span>Subtotal:</span>
                  <span>₹{pricingData.discountedPrice.toLocaleString()}</span>
                </div>
              )}
              
              <div className="breakdown-row">
                <span>Service fee:</span>
                <span>₹{pricingData.serviceFee.toLocaleString()}</span>
              </div>
              <div className="breakdown-row">
                <span>Taxes & charges:</span>
                <span>₹{pricingData.taxes.toLocaleString()}</span>
              </div>
            </div>
            
            <div className="cancellation-info">
              <FaCheck />
              <span>Free cancellation before {new Date(new Date(checkIn).getTime() - 24 * 60 * 60 * 1000).toLocaleDateString()}</span>
            </div>
            <div className="cancellation-detail">
              <div>After {new Date(new Date(checkIn).getTime() - 24 * 60 * 60 * 1000).toLocaleDateString()}: ₹{Math.round(pricingData.finalTotal * 0.8).toLocaleString()}</div>
            </div>
          </div>
        </div>

        {/* Right Column - Guest Information Form */}
        <div className="booking-right-column">
          <form onSubmit={handleSubmit} className="booking-form">
            {/* Guest Information */}
            <div className="form-section">
              <div className="section-header">
                <h3>Guest information</h3>
                {currentUser && autoFilled && (
                  <div className="auto-filled-badge">
                    <FaCheck /> Auto-filled from your profile
                  </div>
                )}
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>First name *</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    className={errors.firstName ? 'error' : ''}
                    required
                  />
                  {errors.firstName && <span className="error-message">{errors.firstName}</span>}
                </div>
                
                <div className="form-group">
                  <label>Last name *</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    className={errors.lastName ? 'error' : ''}
                    required
                  />
                  {errors.lastName && <span className="error-message">{errors.lastName}</span>}
                </div>
              </div>

              <div className="form-group">
                <label>Email address *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={errors.email ? 'error' : ''}
                  required
                />
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>

              <div className="form-group">
                <label>Confirm email address *</label>
                <input
                  type="email"
                  name="confirmEmail"
                  value={formData.confirmEmail}
                  onChange={handleChange}
                  className={errors.confirmEmail ? 'error' : ''}
                  required
                />
                {errors.confirmEmail && <span className="error-message">{errors.confirmEmail}</span>}
              </div>

              <div className="form-group">
                <label>Phone number</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+91 XXXXX XXXXX"
                  className={errors.phone ? 'error' : ''}
                />
                {errors.phone && <span className="error-message">{errors.phone}</span>}
                <span className="field-note">We'll use this to send booking confirmations</span>
              </div>

              <div className="form-group">
                <label>Country/Region *</label>
                <select
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                >
                  <option value="India">India</option>
                  <option value="United States">United States</option>
                  <option value="United Kingdom">United Kingdom</option>
                  <option value="Canada">Canada</option>
                  <option value="Australia">Australia</option>
                </select>
              </div>

              <div className="form-checkboxes">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="receiveUpdates"
                    checked={formData.receiveUpdates}
                    onChange={handleChange}
                  />
                  <span>I want to receive deals and special offers</span>
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="travelingForWork"
                    checked={formData.travelingForWork}
                    onChange={handleChange}
                  />
                  <span>I'm traveling for work</span>
                </label>
              </div>

              <div className="form-radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="bookingForSelf"
                    checked={formData.bookingForSelf}
                    onChange={() => handleRadioChange('bookingForSelf', true)}
                  />
                  <span>Are you booking for yourself?</span>
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="bookingForSomeone"
                    checked={formData.bookingForSomeone}
                    onChange={() => handleRadioChange('bookingForSomeone', true)}
                  />
                  <span>Are you booking for someone else?</span>
                </label>
              </div>
            </div>

            {/* Good to Know - real accommodation details */}
            {(() => {
              const accType = getSafeString(property.type, 'accommodation');
              const freeCancelDate = new Date(new Date(checkIn).getTime() - 24 * 60 * 60 * 1000).toLocaleDateString();
              const items = [];
              if (property.policies?.cancellationPolicy?.trim()) {
                items.push(property.policies.cancellationPolicy.trim());
              } else {
                items.push(`Free cancellation before ${freeCancelDate}`);
              }
              if (property.policies?.paymentPolicy?.trim()) {
                items.push(property.policies.paymentPolicy.trim());
              }
              if (property.bedrooms > 0 || property.bathrooms > 0 || property.maxGuests > 0) {
                const parts = [];
                if (property.bedrooms > 0) parts.push(`${property.bedrooms} ${property.bedrooms === 1 ? 'bedroom' : 'bedrooms'}`);
                if (property.bathrooms > 0) parts.push(`${property.bathrooms} ${property.bathrooms === 1 ? 'bathroom' : 'bathrooms'}`);
                if (property.maxGuests > 0) parts.push(`up to ${property.maxGuests} ${property.maxGuests === 1 ? 'guest' : 'guests'}`);
                if (parts.length) items.push(`This ${accType} has ${parts.join(', ')}`);
              }
              if (property.houseRules?.length > 0) {
                property.houseRules.slice(0, 2).forEach(rule => items.push(rule));
              }
              if (items.length === 0) return null;
              return (
                <div className="form-section good-to-know">
                  <h3>Good to know</h3>
                  <ul>
                    {items.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </div>
              );
            })()}

            {/* Special Requests */}
            <div className="form-section special-requests-section">
              <div className="special-requests-header">
                <h3>Special requests</h3>
                <span className="optional-badge">Optional</span>
              </div>
              <p className="section-description">
                Special requests cannot be guaranteed, but the property will do its best to meet your needs. 
                You can always make a special request after your booking is complete!
              </p>
              <div className="textarea-wrapper">
                <textarea
                  name="specialRequests"
                  value={formData.specialRequests}
                  onChange={handleChange}
                  placeholder="Please write your requests in English or your preferred language (e.g., high floor, quiet room, early check-in, extra pillows, etc.)"
                  maxLength={250}
                  rows={5}
                  className={errors.specialRequests ? 'error' : ''}
                />
                <div className="textarea-footer">
                  <span className="char-count">
                    <span className={formData.specialRequests.length > 200 ? 'warning' : ''}>
                      {formData.specialRequests.length}
                    </span>
                    /250 characters
                  </span>
                </div>
              </div>
              {errors.specialRequests && <span className="error-message">{errors.specialRequests}</span>}
            </div>

            {/* Arrival Time */}
            <div className="form-section">
              <h3>Your arrival time</h3>
              <p className="section-note">Check-in is {property?.checkInTime || checkInTimeDisplay}</p>
              <div className="form-group">
                <select
                  name="arrivalTime"
                  value={formData.arrivalTime}
                  onChange={handleChange}
                >
                  <option value="">Select estimated arrival time</option>
                  <option value="15:00-16:00">3:00 PM - 4:00 PM</option>
                  <option value="16:00-17:00">4:00 PM - 5:00 PM</option>
                  <option value="17:00-18:00">5:00 PM - 6:00 PM</option>
                  <option value="18:00-19:00">6:00 PM - 7:00 PM</option>
                  <option value="19:00-20:00">7:00 PM - 8:00 PM</option>
                  <option value="20:00-21:00">8:00 PM - 9:00 PM</option>
                  <option value="21:00-22:00">9:00 PM - 10:00 PM</option>
                  <option value="22:00-23:00">10:00 PM - 11:00 PM</option>
                </select>
              </div>
            </div>

            {/* Cribs and Extra Beds */}
            <div className="form-section">
              <h3>Cribs and extra beds</h3>
              <p className="section-note">Information about availability and charges</p>
              <div className="form-radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="cribs"
                    checked={formData.cribs}
                    onChange={() => setFormData(prev => ({ ...prev, cribs: !prev.cribs }))}
                  />
                  <span>Two-Bedroom Apartment</span>
                </label>
                <div className="radio-options">
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="cribsOption"
                      value="yes"
                      checked={formData.cribs}
                      onChange={() => setFormData(prev => ({ ...prev, cribs: true }))}
                    />
                    <span>Yes</span>
                  </label>
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="cribsOption"
                      value="no"
                      checked={!formData.cribs}
                      onChange={() => setFormData(prev => ({ ...prev, cribs: false }))}
                    />
                    <span>No</span>
                  </label>
                </div>
              </div>
            </div>

            {/* House Rules */}
            <div className="form-section house-rules">
              <h3>Review House Rules</h3>
              <div className="house-rules-booking-summary">
                <p className="rules-booking-title">This booking:</p>
                <ul>
                  <li><strong>{getSafeString(property.name)}</strong></li>
                  <li>Check-in: {new Date(checkIn).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} ({checkInTimeDisplay}) · Check-out: {new Date(checkOut).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} ({checkOutTimeDisplay})</li>
                  <li>{calculatedNumberOfNights} {calculatedNumberOfNights === 1 ? 'night' : 'nights'} · {guests} {guests === 1 ? 'guest' : 'guests'} · ₹{pricingData.finalTotal.toLocaleString()} total</li>
                </ul>
              </div>
              <p className="rules-note">By continuing, you agree to the booking terms</p>
            </div>

            {/* Submit Button */}
            <button type="submit" className="btn-booking-next" disabled={loading}>
              {loading ? 'Processing...' : 'Next: Final details'}
            </button>

            <button 
              type="button"
              className="booking-conditions-link"
              onClick={() => setShowConditionsModal(true)}
            >
              What are my booking conditions?
            </button>
          </form>
        </div>
      </div>

      {/* Booking Conditions Modal */}
      {showConditionsModal && (
        <div className="modal-overlay" onClick={() => setShowConditionsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Booking Conditions</h2>
              <button 
                className="modal-close"
                onClick={() => setShowConditionsModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {/* This booking summary - actual factors */}
              <div className="condition-section booking-summary-section">
                <h3>📋 This Booking</h3>
                <ul>
                  <li><strong>Property:</strong> {getSafeString(property.name)}</li>
                  <li><strong>Check-in:</strong> {new Date(checkIn).toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })} — {checkInTimeDisplay}</li>
                  <li><strong>Check-out:</strong> {new Date(checkOut).toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })} — {checkOutTimeDisplay}</li>
                  <li><strong>Nights:</strong> {calculatedNumberOfNights} {calculatedNumberOfNights === 1 ? 'night' : 'nights'}</li>
                  <li><strong>Guests:</strong> {guests} {guests === 1 ? 'adult' : 'adults'}</li>
                  <li><strong>Total amount:</strong> ₹{pricingData.finalTotal.toLocaleString()}</li>
                </ul>
              </div>

              <div className="condition-section">
                <h3>📅 Check-in & Check-out</h3>
                <ul>
                  <li><strong>Check-in time:</strong> {property?.checkInTime || checkInTimeDisplay}</li>
                  <li><strong>Check-out time:</strong> {property?.checkOutTime || checkOutTimeDisplay}</li>
                  <li>Early check-in and late check-out are subject to availability</li>
                  <li>Please inform the property of your expected arrival time</li>
                </ul>
              </div>

              <div className="condition-section">
                <h3>💳 Payment & Cancellation</h3>
                <ul>
                  <li><strong>Payment:</strong> Full payment of ₹{pricingData.finalTotal.toLocaleString()} required at the time of booking</li>
                  <li><strong>Free Cancellation:</strong> Cancel before {new Date(new Date(checkIn).getTime() - 24 * 60 * 60 * 1000).toLocaleDateString()} for a full refund</li>
                  <li><strong>Late Cancellation:</strong> After the free cancellation period, you'll be charged ₹{Math.round(pricingData.finalTotal * 0.8).toLocaleString()} (80% of total)</li>
                  <li><strong>No-show:</strong> Full amount (₹{pricingData.finalTotal.toLocaleString()}) will be charged</li>
                </ul>
              </div>

              <div className="condition-section">
                <h3>👥 Guest Policy</h3>
                <ul>
                  <li>Maximum {guests} {guests === 1 ? 'guest' : 'guests'} per booking (this booking)</li>
                  <li>Valid ID proof required at check-in</li>
                  <li>Children of all ages are welcome</li>
                  <li>Extra beds/cribs available on request (charges may apply)</li>
                </ul>
              </div>

              <div className="condition-section">
                <h3>🏠 Property Rules</h3>
                <ul>
                  <li>No smoking inside the property</li>
                  <li>No parties or events allowed</li>
                  <li>Pets are not allowed (unless specified)</li>
                  <li>Quiet hours: 10:00 PM - 8:00 AM</li>
                  <li>Guests are responsible for any damage to the property</li>
                </ul>
              </div>

              <div className="condition-section">
                <h3>✨ Special Requests</h3>
                <ul>
                  <li>Special requests are subject to availability</li>
                  <li>The property will do its best to accommodate your requests</li>
                  <li>Special requests cannot be guaranteed</li>
                  <li>Additional charges may apply for certain requests</li>
                </ul>
              </div>

              <div className="condition-section">
                <h3>📞 Contact & Support</h3>
                <ul>
                  <li>24/7 customer support available</li>
                  <li>Property contact details will be provided after booking</li>
                  <li>For any issues, contact us immediately</li>
                </ul>
              </div>

              <div className="condition-section important-note">
                <h3>⚠️ Important Note</h3>
                <p>
                  By completing this booking, you agree to these terms and conditions. 
                  Please read them carefully before proceeding. If you have any questions, 
                  please contact our customer support team.
                </p>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn-modal-close"
                onClick={() => setShowConditionsModal(false)}
              >
                I Understand
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Offer Confirmation Popup */}
      {showOfferConfirmation && effectiveDiscount && (
        <div className="offer-confirmation-overlay">
          <div className="offer-confirmation-modal">
            <div className="offer-confirmation-header">
              <h3>🎉 Confirm Your Special Offer</h3>
            </div>
            
            <div className="offer-confirmation-content">
              <div className="offer-details">
                <div className="offer-badge-large">
                  {effectiveDiscount.badge || 'Special Offer'}
                </div>
                <h4 className="offer-title-large">{effectiveDiscount.title}</h4>
                {effectiveDiscount.description && (
                  <p className="offer-description">{effectiveDiscount.description}</p>
                )}
              </div>
              
              <div className="savings-summary-large">
                <div className="savings-amount-large">
                  You Save ₹{pricingData.discountAmount.toLocaleString()}
                </div>
                <div className="savings-percentage">
                  ({effectiveDiscount.savingsPercentage != null ? effectiveDiscount.savingsPercentage : Math.round((pricingData.discountAmount / pricingData.originalPrice) * 100)}% off original price)
                </div>
              </div>
              
              <div className="price-comparison">
                <div className="price-row">
                  <span>Original Price:</span>
                  <span className="original-price-large">₹{pricingData.originalPrice.toLocaleString()}</span>
                </div>
                <div className="price-row discount-price-row">
                  <span>Your Price:</span>
                  <span className="discounted-price-large">₹{pricingData.discountedPrice.toLocaleString()}</span>
                </div>
              </div>
              
              {discount.validUntil && (
                <div className="offer-validity-warning">
                  ⏰ This offer expires on {new Date(discount.validUntil).toLocaleDateString()}
                </div>
              )}
              
              {discount.termsAndConditions && (
                <div className="offer-terms">
                  <p><strong>Terms & Conditions:</strong></p>
                  <p>{discount.termsAndConditions}</p>
                </div>
              )}
            </div>
            
            <div className="offer-confirmation-actions">
              <button 
                className="btn-cancel-offer"
                onClick={() => handleOfferConfirmation(false)}
              >
                Cancel
              </button>
              <button 
                className="btn-confirm-offer"
                onClick={() => handleOfferConfirmation(true)}
              >
                Confirm Booking with Offer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingPage;

